import React from "react";
import { Form, InputNumber, Select, Space, Button, Input, Popconfirm } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import {
  WarehouseLoggedHours,
  WarehouseLoggedHoursStatus,
} from "services/BillingEngineService/interfaces/entities/WarehouseLoggedHours";
import { hourOptions, minuteOptions } from "../constants";
import colors from "css/colors";

const { Option } = Select;

interface TableCell {
  record: WarehouseLoggedHours;
}

interface EditableCell extends TableCell {
  editing: boolean;
}

function WorkersCountCell({ editing, record }: EditableCell) {
  if (editing) {
    return (
      <Form.Item
        name="workersCount"
        initialValue={record.workersCount}
        rules={[
          {
            required: true,
            message: "Please select the number of workers",
          },
        ]}
      >
        <InputNumber />
      </Form.Item>
    );
  }

  return <span>{record.workersCount}</span>;
}

const STATUS_LABEL_MAP: Record<WarehouseLoggedHoursStatus, string> = {
  processed: "Processed",
  pending: "Pending",
};

const STATUS_COLOR_MAP: Record<WarehouseLoggedHoursStatus, string> = {
  processed: colors.GREEN,
  pending: colors.YELLOW,
};

function StatusCell({ record }: TableCell) {
  const statusText = STATUS_LABEL_MAP[record.status];
  const color = STATUS_COLOR_MAP[record.status];

  return <span style={{ color }}>{statusText}</span>;
}

function HoursCell({ editing, record }: EditableCell) {
  if (editing) {
    return (
      <Space size="middle">
        <Form.Item
          name="hoursWorked"
          initialValue={record.hoursWorked}
          rules={[
            {
              required: true,
              message: "Please select the number of hours worked",
            },
          ]}
        >
          <Select>
            {hourOptions.map((option) => (
              <Option key={option} value={option}>
                {option}h
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="minutesWorked" initialValue={record.minutesWorked}>
          <Select>
            {minuteOptions.map((option) => (
              <Option key={option} value={option}>
                {option} mins
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Space>
    );
  }

  return (
    <span>
      {record.hoursWorked && `${record.hoursWorked}h`} {record.minutesWorked && `${record.minutesWorked}m`}
    </span>
  );
}

interface ExpandableRowCell {
  onExpand: (record: WarehouseLoggedHours, e: any) => void;
  record: WarehouseLoggedHours;
  expanded: boolean;
  editing?: boolean;
}

function ExpandNotesCell({ onExpand, record, expanded, editing }: ExpandableRowCell) {
  if (editing) {
    return null;
  }

  if (!record.notes) {
    return <span>N/A</span>;
  }

  return (
    <Button type="text" style={{ color: colors.BLUE }} onClick={(e) => onExpand({ ...record }, e)}>
      {expanded ? "Hide" : "Show"}
    </Button>
  );
}

function NotesCell({ editing, record }: EditableCell) {
  if (editing) {
    return (
      <Form.Item name="notes" initialValue={record.notes}>
        <Input.TextArea placeholder="Add notes or comments regarding this shift." />
      </Form.Item>
    );
  }

  return <span>{record.notes}</span>;
}

interface EditActionsCell extends EditableCell {
  save: (record: WarehouseLoggedHours) => void;
  cancel: (record: WarehouseLoggedHours) => void;
  remove: (record: WarehouseLoggedHours) => void;
  edit: (record: WarehouseLoggedHours) => void;
}

function EditActionsCell({ record, editing, save, cancel, remove, edit }: EditActionsCell) {
  const disabled = record.status === "processed";

  if (editing) {
    return (
      <span>
        <Button style={{ color: colors.BLUE }} type="text" onClick={() => save(record)}>
          Save
        </Button>
        <Button style={{ color: colors.GREY_DARK }} type="text" onClick={() => cancel(record)}>
          Cancel
        </Button>
      </span>
    );
  }

  return (
    <span>
      <Button disabled={disabled} type="text" style={{ color: colors.BLUE }} onClick={() => edit(record)}>
        <EditOutlined />
      </Button>

      <Popconfirm
        disabled={disabled}
        title="Are you sure you want to delete this record?"
        onConfirm={() => remove(record)}
        onCancel={() => {}}
        okText="Yes"
        cancelText="No"
      >
        <Button disabled={disabled} type="text" style={{ color: colors.RED }}>
          <DeleteOutlined />
        </Button>
      </Popconfirm>
    </span>
  );
}

export { WorkersCountCell, StatusCell, HoursCell, ExpandNotesCell, NotesCell, EditActionsCell };
